import { provideHttpClient } from '@angular/common/http';
import {
    ApplicationConfig, importProvidersFrom,
    inject,
    isDevMode,
    provideAppInitializer,
} from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats, provideNativeDateAdapter } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter, withHashLocation,
    withInMemoryScrolling,
    withPreloading,
    withRouterConfig,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { AgaveModule, AgaveService } from 'tq-agave';
import { GlobalsService } from './globals.service';
import { agaveConfigurationFactory } from './core/config/config.utils';
import { AppConfigurationService } from './core/config/app-configuration.service';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';

import { PlotlyModule } from 'angular-plotly.js';
import * as PlotlyJS from 'plotly.js-dist-min';
import { DatePipe, JsonPipe } from '@angular/common';

export const ISO_DATE_FORMATS: MatDateFormats = {
    parse: {
        dateInput: 'yyyy-MM-dd',
    },
    display: {
        dateInput: 'yyyy-MM-dd',
        monthYearLabel: 'yyyy MMM',
        dateA11yLabel: 'yyyy-MM-dd',
        monthYearA11yLabel: 'yyyy MMMM',
    },
};

export const appConfig: ApplicationConfig = {
    providers: [
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
        { provide: MAT_DATE_FORMATS, useValue: ISO_DATE_FORMATS },
        provideAnimations(),
        DatePipe,
        JsonPipe,
        importProvidersFrom(PlotlyModule.forRoot(PlotlyJS)),
        provideHttpClient(),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
            withRouterConfig({ paramsInheritanceStrategy: 'always' }),
            withHashLocation()
        ),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en-US',
                        label: 'English',
                    },
                    {
                        id: 'it-IT',
                        label: 'Italian',
                    },
                ],
                defaultLang: 'en-US',
                fallbackLang: 'en-US',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideTranslocoLocale(),
        provideAppInitializer(async () => {
            const translocoService = inject(TranslocoService);
            const defaultLang = translocoService.getDefaultLang();
            translocoService.setActiveLang(defaultLang);

            const dateAdapter = inject(DateAdapter);
            translocoService.langChanges$.subscribe(locale => {
                if (locale) {
                    dateAdapter.setLocale(locale);
                }
            });

            const appConfigurationService = inject(AppConfigurationService);
            const agaveService = inject(AgaveService);
            return await Promise.all([
                agaveConfigurationFactory(appConfigurationService, agaveService),
                firstValueFrom(translocoService.load(defaultLang))
            ]);
        }),

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            fuse: {
                layout: 'classy',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
        importProvidersFrom(AgaveModule.forRoot(GlobalsService)),
    ]
};
